import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminService from '../../services/admin.service';

export const fetchAdminDashboard = createAsyncThunk(
  'admin/fetchDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getDashboard();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch dashboard' });
    }
  }
);

export const fetchAdminUsers = createAsyncThunk(
  'admin/fetchUsers',
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getUsers(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch users' });
    }
  }
);

export const fetchAdminVendors = createAsyncThunk(
  'admin/fetchVendors',
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getVendors(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch vendors' });
    }
  }
);

export const fetchAdminMachines = createAsyncThunk(
  'admin/fetchMachines',
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getMachines(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch machines' });
    }
  }
);

export const fetchAdminBookings = createAsyncThunk(
  'admin/fetchBookings',
  async (params, { rejectWithValue }) => {
    try {
      const response = await adminService.getBookings(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to fetch bookings' });
    }
  }
);

export const updateVendorKYC = createAsyncThunk(
  'admin/updateVendorKYC',
  async ({ vendorId, status, rejectionReason }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateVendorKYC(vendorId, status, rejectionReason);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update KYC' });
    }
  }
);

export const updateVendorStatus = createAsyncThunk(
  'admin/updateVendorStatus',
  async ({ vendorId, isActive }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateVendorStatus(vendorId, isActive);
      return { vendorId, isActive, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update vendor status' });
    }
  }
);

export const deleteVendor = createAsyncThunk(
  'admin/deleteVendor',
  async (vendorId, { rejectWithValue }) => {
    try {
      await adminService.deleteVendor(vendorId);
      return vendorId;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delete vendor' });
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'admin/updateUserStatus',
  async ({ userId, isActive }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUserStatus(userId, isActive);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update user' });
    }
  }
);

export const deleteUser = createAsyncThunk(
  'admin/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      await adminService.deleteUser(userId);
      return userId;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delete user' });
    }
  }
);

export const updateMachineStatus = createAsyncThunk(
  'admin/updateMachineStatus',
  async ({ machineId, status, rejectionReason }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateMachineStatus(machineId, status, rejectionReason);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to update machine' });
    }
  }
);

export const deleteMachine = createAsyncThunk(
  'admin/deleteMachine',
  async (machineId, { rejectWithValue }) => {
    try {
      await adminService.deleteMachine(machineId);
      return machineId;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Failed to delete machine' });
    }
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    dashboard: null,
    users: [],
    vendors: [],
    machines: [],
    bookings: [],
    pagination: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => { state.error = null; }
  },
  extraReducers: (builder) => {
    const pending = (state) => { state.loading = true; state.error = null; };
    const rejected = (state, action) => { state.loading = false; state.error = action.payload?.message; };

    builder
      .addCase(fetchAdminDashboard.pending, pending)
      .addCase(fetchAdminDashboard.fulfilled, (state, action) => { state.loading = false; state.dashboard = action.payload; })
      .addCase(fetchAdminDashboard.rejected, rejected)
      
      .addCase(fetchAdminUsers.pending, pending)
      .addCase(fetchAdminUsers.fulfilled, (state, action) => { state.loading = false; state.users = action.payload.users; state.pagination = action.payload.pagination; })
      .addCase(fetchAdminUsers.rejected, rejected)
      
      .addCase(fetchAdminVendors.pending, pending)
      .addCase(fetchAdminVendors.fulfilled, (state, action) => { state.loading = false; state.vendors = action.payload.vendors; state.pagination = action.payload.pagination; })
      .addCase(fetchAdminVendors.rejected, rejected)
      
      .addCase(fetchAdminMachines.pending, pending)
      .addCase(fetchAdminMachines.fulfilled, (state, action) => { state.loading = false; state.machines = action.payload.machines; state.pagination = action.payload.pagination; })
      .addCase(fetchAdminMachines.rejected, rejected)
      
      .addCase(fetchAdminBookings.pending, pending)
      .addCase(fetchAdminBookings.fulfilled, (state, action) => { state.loading = false; state.bookings = action.payload.bookings; state.pagination = action.payload.pagination; })
      .addCase(fetchAdminBookings.rejected, rejected)
      
      .addCase(updateVendorKYC.fulfilled, (state, action) => {
        const idx = state.vendors.findIndex(v => v._id === action.payload.vendor?._id);
        if (idx !== -1) state.vendors[idx] = action.payload.vendor;
      })
      .addCase(updateVendorStatus.fulfilled, (state, action) => {
        const idx = state.vendors.findIndex(v => v._id === action.payload.vendorId);
        if (idx !== -1) state.vendors[idx] = { ...state.vendors[idx], user: { ...state.vendors[idx].user, isActive: action.payload.isActive } };
      })
      .addCase(deleteVendor.fulfilled, (state, action) => {
        state.vendors = state.vendors.filter(v => v._id !== action.payload);
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        const idx = state.users.findIndex(u => u._id === action.payload.user?._id);
        if (idx !== -1) state.users[idx] = action.payload.user;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter(u => u._id !== action.payload);
      })
      .addCase(updateMachineStatus.fulfilled, (state, action) => {
        const idx = state.machines.findIndex(m => m._id === action.payload.machine?._id);
        if (idx !== -1) state.machines[idx] = action.payload.machine;
      })
      .addCase(deleteMachine.fulfilled, (state, action) => {
        state.machines = state.machines.filter(m => m._id !== action.payload);
      });
  },
});

export const { clearError } = adminSlice.actions;
export default adminSlice.reducer;
